'use client'

import { getSafeProductName } from '@/feature/product/services/product.filtering'
import { toProductPage } from '@/feature/product/services/product.routing'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { TooltipOnCroppedContent } from '@/ui/tooltips/tooltip-on-cropped-content/TooltipOnCroppedContent.client'
import type { ProductRowProps } from '../ProductRow.client'

import { createDisplayProductScreenIntent } from '@/feature/product/services/product.intents'
import { Link } from '@/ui/links/link/Link.client'

type ProductRowContentProps = {
  isInMiniCart: boolean
  isInDrawer: boolean
  product: NonNullable<ProductRowProps['product']>
  onLinkPress?: () => void | Promise<void>
}

export function ProductRowProductDetails({
  isInMiniCart,
  isInDrawer,
  product,
  onLinkPress,
}: ProductRowContentProps) {
  const { t } = useTranslation(['generic', 'productRow'])
  const productName = getSafeProductName(product.name)

  return (
    <>
      <div
        className={twMerge(
          'min-w-full flex-1 tablet:max-w-[21.25rem] desktop-md:min-w-20',
          !isInDrawer && 'desktop-md:min-w-20',
        )}
        data-testid="product-card/details"
      >
        <span className="inline-flex flex-col gap-1">
          {product.brand && (
            <span
              className="font-bold uppercase leading-light text-brand-2-900"
              data-testid="product-card/brand"
            >
              {product.brand.name}
            </span>
          )}
          <Link
            variant="free"
            underlined="hover"
            href={toProductPage(product.urlKey)}
            onPress={onLinkPress}
            intent={product.urlKey ? createDisplayProductScreenIntent(product.urlKey) : undefined}
          >
            {isInMiniCart ? (
              <span
                className="line-clamp-2 text-left text-lg leading-light"
                data-testid="product-card/product-name"
              >
                {productName}
              </span>
            ) : (
              <TooltipOnCroppedContent tooltipText={productName} placement="bottom">
                <span
                  className="line-clamp-2 text-left text-lg leading-light"
                  data-testid="product-card/product-name"
                >
                  {productName}
                </span>
              </TooltipOnCroppedContent>
            )}
          </Link>
          <span className="text-sm text-brand-2-900">
            {t('productRow:ref', { ref: product.reference })}
          </span>
        </span>
      </div>
    </>
  )
}
